import React, { FC, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Layout, Row, Col } from 'antd';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { EmptyState } from '../empty_state/EmptyState';
import { getAuditFieldNameByKey } from '../../utils/AuditUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import PartEquipmentNameWithType from '../equipment_per_stock_location_row_display/PartEquipmentNameWithType';
import { UserName } from '../name_component/user/UserNameComponent';
import DateTimeHover from '../date_time_component/DateTime';
import { fetchPurchaseOrderAudits } from '../../thunks/purchase_order_audits_thunks';
import { PURCHASE_ORDER_AUDITS_CRUD_ACTION_CREATORS } from '../../actions/purchase_order_audits_actions';
import { PURCHASE_ORDER_STATUS_DISPLAY_NAMES } from '../../constants/PurchaseOrderStatuses';

const { Content } = Layout;

const TARGET_COLLECTION_NAME = 'purchaseOrderAuditsIndex';

const fieldMap = {
	// Purchase order level
	name: 'Name',
	description: 'Description',
	paymentTerms: 'Payment Terms',
	shippingTerms: 'Shipping Terms',
	partEquipmentVendorId: 'Vendor',
	brandId: 'Brand',
	freightTotalBeforeTax: 'Freight',
	miscTotalBeforeTax: 'Misc',
	totalBeforeTax: 'Total Before Tax',
	tax: 'Tax',
	totalCost: 'Total Cost',
	currencyId: 'Currency',
	internalNotes: 'Internal Notes',
	integratorName: 'Integrator',
	integratorStatus: 'Integrator Status',
	dueDate: 'Due Date',
	note: 'Note',
	sentTo: 'Sent To',
	// Shared between the purchase order and its line items
	stockLocationId: 'Stock Location',
	locationId: 'Location',
	// Line item level
	viaStockLocationId: 'Via Stock Location',
	warrantyAssetId: 'Warranty Asset',
	glCode: 'GL Code',
	partId: 'Part',
	partCatalogId: 'Part Catalog',
	partUomId: 'Part UOM',
	partCurrencyId: 'Part Currency',
	partQuantity: 'Quantity',
	partCost: 'Cost',
	partReceivedQuantity: 'Received Quantity',
	equipmentTypeId: 'Equipment Type',
	equipmentCatalogId: 'Equipment Catalog',
	equipmentUomId: 'Equipment UOM',
	equipmentCurrencyId: 'Equipment Currency',
	equipmentQuantity: 'Quantity',
	equipmentCost: 'Cost',
	equipmentReceivedQuantity: 'Received Quantity',
	// Receipt level
	receiptNumber: 'Receipt Number',
	receivedQuantity: 'Received Quantity',
	pricePerQuantity: 'Price Per Quantity',
	invoiceNumber: 'Invoice Number',
	invoiceCurrency: 'Invoice Currency',
	invoiceTotal: 'Invoice Total',
	exchangeRate: 'Exchange Rate',
	invoiceTotalPostExchange: 'Invoice Total (Post Exchange)',
	invoiceAttachments: 'Invoice PDF',
};

// Fields the backend stores as an id but hydrates into a named entity on the audit row, so the trail can
// show "Main Warehouse" instead of "70".
const ID_FIELD_ENTITY_MAP = {
	stockLocationId: 'stockLocation.name',
	locationId: 'location.name',
};

const resolveStatusLabel = (status) => {
	if (!status) return '--';
	return PURCHASE_ORDER_STATUS_DISPLAY_NAMES[status] || status;
};

const formatFieldValue = (value, fallback) => {
	if (value === null || value === undefined) return fallback;
	return value;
};

const renderFieldChange = (field, record) => {
	const label = fieldMap[field.fieldName] || field.fieldName;

	const entityPath = ID_FIELD_ENTITY_MAP[field.fieldName];
	const resolvedNewValue = entityPath
		? formatFieldValue(nullSafeGet(entityPath, record), formatFieldValue(field.newValue, '--'))
		: formatFieldValue(field.newValue, '--');

	// Events that set a value for the first time — a note being added, a purchase order being emailed,
	// a receipt being created — have no previous value, so the before/after arrow would read "-- -> x".
	const hasOldValue = field.oldValue !== null && field.oldValue !== undefined;

	return (
		<span
			key={field.fieldName}
			className="inline-block rounded-md bg-violet-100 p-2 text-sm text-gray-500"
		>
			{hasOldValue ? (
				<>
					{label} : {field.oldValue} <ArrowRightOutlined /> {resolvedNewValue}
				</>
			) : (
				<>
					{label} : {resolvedNewValue}
				</>
			)}
		</span>
	);
};

const renderStatusChange = (record) => {
	const fromStatus = nullSafeGet('fromStatus', record);
	const toStatus = nullSafeGet('toStatus', record);
	if (!fromStatus && !toStatus) return null;

	return (
		<span
			key="statusChange"
			className="inline-block rounded-md bg-violet-100 p-2 text-sm text-gray-500"
		>
			Status : {resolveStatusLabel(fromStatus)} <ArrowRightOutlined />{' '}
			{resolveStatusLabel(toStatus)}
		</span>
	);
};

const renderLineItemDetail = (record) => {
	const hasPart = nullSafeGet('part', record) || nullSafeGet('partId', record);
	const hasEquipment =
		nullSafeGet('equipmentType', record) || nullSafeGet('equipmentTypeId', record);

	if (!hasPart && !hasEquipment) return '--';

	return <PartEquipmentNameWithType record={record} backText="Back to Audits" />;
};

const PurchaseOrderAuditsTab: FC<any> = ({
	match,
	purchaseOrderAudits,
	updateFilters,
	clearAndUpdateFilters,
}) => {
	const purchaseOrderId = useMemo(() => match.params.id, [match.params.id]);

	const columns = useMemo(
		() => [
			{
				title: 'Audit Type',
				dataIndex: 'auditType',
				render: (_) => getAuditFieldNameByKey(_),
			},
			{
				title: 'Updated By',
				dataIndex: 'createdByContact',
				render: (_) => (_ ? <UserName contact={_} mode="inline" placement="top" /> : '--'),
			},
			{
				title: 'Updated At',
				dataIndex: 'createdAt',
				render: (_) => (_ ? <DateTimeHover timestamp={_} /> : '--'),
			},
			{
				title: 'Line Item',
				dataIndex: 'lineItemId',
				width: 200,
				render: (_, record) => renderLineItemDetail(record),
			},
			{
				title: 'Changes',
				dataIndex: 'fieldValueChanges',
				render: (fieldValueChanges, record) => {
					const statusChange = renderStatusChange(record);
					const fieldChanges =
						fieldValueChanges && fieldValueChanges.length > 0
							? fieldValueChanges.map((field) => renderFieldChange(field, record))
							: [];
					const allChanges = [statusChange].concat(fieldChanges).filter(Boolean);
					return allChanges.length > 0 ? (
						<div className="flex flex-wrap gap-2">{allChanges}</div>
					) : (
						'--'
					);
				},
			},
		],
		[]
	);

	const fetchData = useCallback(
		(params, targetCollectionName, pagination, sorting, filters) => {
			return fetchPurchaseOrderAudits(
				purchaseOrderId,
				targetCollectionName,
				pagination,
				sorting,
				filters
			);
		},
		[purchaseOrderId]
	);

	return (
		<Content style={{ padding: '0 0.5em' }}>
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={24}>
					<PaginatedReduxTableWithHeader
						targetCollectionName={TARGET_COLLECTION_NAME}
						stateSlice={purchaseOrderAudits}
						updateFilters={updateFilters}
						clearAndUpdateFilters={clearAndUpdateFilters}
						filterConfig={[]}
						rightActions={null}
						entityCollectionName="purchase_order_audits"
						tableColumns={columns}
						fetchData={fetchData}
						showHeader={true}
						hasDefaultHeaderPage={false}
						initialPagination={{ current: 1, pageSize: 10 }}
						initialSorters={{}}
						hideFilters
						hideSearch
						keyAccessor={(el) => el.id}
						emptyState={
							<EmptyState
								graphic={
									<img
										style={{ marginBottom: 8 }}
										src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
										alt="No audits found!"
									/>
								}
								headline={'No audit records found.'}
								body={null}
							/>
						}
						ignoreLocationQueryParams
					/>
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state) => ({
	purchaseOrderAudits: state.purchase_order_audits,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollectionName) =>
		dispatch(
			PURCHASE_ORDER_AUDITS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			PURCHASE_ORDER_AUDITS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(PurchaseOrderAuditsTab)
);
